/**
 * Turning a failed API response into a message worth reading.
 *
 * The services used to switch on the HTTP status alone and throw a fixed key per
 * status. That collapses every distinct reason into one sentence: the backend
 * answers a duplicate account name and a password that breaks the policy with
 * the same 400, so both surfaced as "Invalid request" and the operator had no
 * way to know which rule they had broken.
 *
 * The backend now names the reason in a `code` field. Read that first, fall back
 * to the status, and keep the server's own sentence as a detail so a code we do
 * not know about yet still says something true.
 */

export interface ApiErrorBody {
  error?: string
  code?: string
}

export class ApiError extends Error {
  constructor(
    /** i18n key for the message to show, e.g. `errors.accountAlreadyExists`. */
    message: string,
    public status: number,
    /** Machine-readable reason from the backend, when it sent one. */
    public code?: string,
    /** The backend's own sentence, for detail lines and logs. */
    public detail?: string
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/** Reasons the backend names, mapped to the i18n key that explains each one. */
const CODE_KEYS: Record<string, string> = {
  USER_ALREADY_EXISTS: 'errors.accountAlreadyExists',
  PROJECT_ALREADY_EXISTS: 'errors.projectAlreadyExists',
  ALREADY_PROJECT_MEMBER: 'errors.alreadyProjectMember',
  USER_NOT_FOUND: 'errors.userNotFound',
  PROJECT_NOT_FOUND: 'errors.projectNotFound',
  NOT_PROJECT_MEMBER: 'errors.notProjectMember',
  PASSWORD_POLICY: 'errors.passwordPolicy',
  CANNOT_MODIFY_SELF: 'errors.cannotModifySelf',
  ONLY_LOCAL_USER: 'errors.onlyLocalUser',
  INVALID_ROLE: 'errors.invalidRole',
  LICENSE_EXPIRED: 'errors.licenseExpired',
  RESET_TOKEN_INVALID: 'errors.resetTokenInvalid',
  PASSWORD_UNCHANGED: 'errors.passwordUnchanged',
  WRONG_PASSWORD: 'errors.wrongPassword',
}

/** Last resort when the backend named no reason. */
const STATUS_KEYS: Record<number, string> = {
  400: 'errors.badRequest',
  401: 'errors.unauthorized',
  403: 'errors.forbidden',
  404: 'errors.notFound',
  409: 'errors.conflict',
  500: 'errors.serverError',
}

/**
 * Reads a failed response and builds the error to throw.
 *
 * `fallbackKey` covers a status this call knows better than the table does, such
 * as `errors.createUserFailed` for an unexpected status on user creation.
 */
export async function toApiError(response: Response, fallbackKey: string): Promise<ApiError> {
  let body: ApiErrorBody = {}
  try {
    const text = await response.text()
    if (text) {
      body = JSON.parse(text) as ApiErrorBody
    }
  } catch {
    // A non-JSON body (a proxy's HTML error page, say) leaves body empty; the
    // status and fallback key still produce a sensible message.
  }

  const key =
    (body.code && CODE_KEYS[body.code]) || STATUS_KEYS[response.status] || fallbackKey

  return new ApiError(key, response.status, body.code, body.error)
}
